import { Trans } from "@lingui/react/macro";
import styled, { useTheme } from "styled-components";
import useMeasure from "react-use-measure";
import { useState } from "react";
import { Icon, Stack, Text, useParsedSearchParams } from "@fidelix/fx-miranda";
import { ProcessPicture } from "./process-picture";
import { GraphicsHeader } from "./graphics-header";
import { MainPanelLayout } from "../common/page-layout";
import { GraphicsDataProvider } from "./graphics-context";
import { HistoryReplayContainer } from "./history/history-replay-container";
import { HistoryReplayDataProvider } from "./history/history-replay-context";
import { useWindowResizeEvent } from "../utils/observe";
import { ToggleHistoryReplayButton } from "./history/toggle-history-replay-button";
import { RealtimePointUpdatesProvider } from "../contexts/realtime-point-values-context";

export function GraphicsView() {
  const { parsedParams } = useParsedSearchParams({
    graphicsId: { type: "number", defaultValue: undefined },
  });

  return (
    <GraphicsMainLayout>
      {parsedParams.graphicsId ? (
        <GraphicsDataProvider>
          <GraphicsHeader />
          <InnerGraphicsView />
        </GraphicsDataProvider>
      ) : (
        <PlaceHolder align="center" justify="center">
          <Stack axis="y" align="center">
            <PlaceHolderIcon align="center" justify="center">
              <Icon color="white" icon="dashboard" size={48} />
            </PlaceHolderIcon>
            <Text variant="subtitle1">
              <Trans>No graphics selected</Trans>
            </Text>
            <Text variant="bodyLightItalic">
              <Trans>Select a graphic from the left panel to get started</Trans>
            </Text>
          </Stack>
        </PlaceHolder>
      )}
    </GraphicsMainLayout>
  );
}

// Minimum sizes for process picture and history replay panel in pixels
export const minReplayContainerHeight = 130;
const minProcessPictureHeight = 200;

export function InnerGraphicsView() {
  const { parsedParams } = useParsedSearchParams({
    replay: { parse: (p) => p.get("replay") === "true" },
    graphicsId: { type: "number", defaultValue: undefined },
  });
  const theme = useTheme();

  const [resizedHeight, setResizedHeight] = useState<number | undefined>();
  const [isResizing, setIsResizing] = useState(false);

  const { innerHeight } = useWindowResizeEvent();
  const [containerRef, { top }] = useMeasure();
  const marginBottom = parsedParams.replay ? minReplayContainerHeight : 0;
  const containerHeight = innerHeight - top - theme.spaceNumber("medium");

  // Calculate the portion of the process picture height in pixels
  const processPictureHeight = parsedParams.replay
    ? (resizedHeight ?? containerHeight)
    : containerHeight;
  // Calculate the remaining height for the history replay panel
  const replayContainerHeight = Math.max(
    containerHeight - processPictureHeight - theme.spaceNumber("small"),
    minReplayContainerHeight,
  );

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const newHeight = containerHeight - (containerHeight - e.clientY) - top;
    if (newHeight <= minProcessPictureHeight || newHeight >= containerHeight) {
      return;
    }
    setResizedHeight(newHeight);
  };

  return (
    <RealtimePointUpdatesProvider>
      <Container
        ref={containerRef}
        onMouseLeave={() => setIsResizing(false)}
        onMouseUp={() => setIsResizing(false)}
        onMouseMove={isResizing ? onMouseMove : undefined}
        style={{
          height: containerHeight,
          maxHeight: containerHeight,
          overflow: "hidden",
        }}
        isFeature={false}
      >
        <ProcessPictureContainer
          style={{
            height: processPictureHeight,
            maxHeight: containerHeight - marginBottom,
            marginBottom: parsedParams.replay ? theme.space("small") : 0,
          }}
        >
          <ProcessPicture />
          <ToggleHistoryReplayButton
            resetContainerHeight={() => setResizedHeight(containerHeight)}
            replayContainerHeight={replayContainerHeight}
          />
        </ProcessPictureContainer>
        {parsedParams.replay && (
          <HistoryReplayDataProvider>
            <HistoryReplayContainer
              isResizing={isResizing}
              height={replayContainerHeight}
            />
            <ResizeButtonContainer>
              <ResizeButton
                onMouseDown={() => setIsResizing(true)}
                style={{
                  bottom: replayContainerHeight - 2,
                  background: isResizing
                    ? theme.colors.neutralBackground
                    : undefined,
                }}
              >
                <Icon icon="dragHandleHorizontal" />
              </ResizeButton>
            </ResizeButtonContainer>
          </HistoryReplayDataProvider>
        )}
      </Container>
    </RealtimePointUpdatesProvider>
  );
}

const Container = styled.div<{ isFeature: boolean }>`
  display: flex;
  flex-direction: column;
  overflow-y: ${(p) => (p.isFeature ? "auto" : "")};
`;

const ResizeButtonContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  background: red;
`;
const ResizeButton = styled.button`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${(p) => p.theme.colors.mutedNeutralBackground};
  width: 84px;
  height: 24px;
  border: 1px solid ${(p) => p.theme.colors.lightNeutral};
  border-radius: 30px;
  box-shadow: 1px 1px 2px ${(p) => p.theme.colors.black}26;
  cursor: ns-resize;
`;
// Use custom paddings and gap for the graphics view since we want to maximize
// the usage of space
export const GraphicsMainLayout = styled(MainPanelLayout)`
  padding-top: ${(p) => p.theme.space("small")};
  padding-bottom: ${(p) => p.theme.space("small")};
  gap: ${(p) => p.theme.space("small")};
  // Set the layout container width and height to 1 since we don't want this view
  // to stretch beyond the screen and have scrollbars break the layout
  height: 1;
  width: 1;
`;
const ProcessPictureContainer = styled.div`
  position: sticky;
  box-sizing: border-box;
  overflow: hidden;
  margin-top: 0px;
  padding-top: 0px;
`;

const PlaceHolder = styled(Stack)`
  height: 100%;
  background: #f6f6f6;
  > div {
    width: 220px;
    text-align: center;
  }
`;

const PlaceHolderIcon = styled(Stack)`
  background: #33333333;
  width: 145px;
  height: 145px;
  border-radius: ${(p) => p.theme.radius("xlarge")};
  > svg {
    height: 80% !important;
    width: 80% !important;
  }
`;
